/**
 * EnablePushNotifications
 *
 * Sits inside CareDiscussionRoomDetail. Handles the full lifecycle of
 * *this device's* Care Rooms push subscription.
 *
 * Behavioural rules (Phase 2):
 *  - Reconciles on mount by comparing browser subscription vs. backend
 *    /status endpoint. NEVER triggers `Notification.requestPermission()`
 *    without the user pressing the Enable button.
 *  - If backend is soft-disabled (`readiness.ready === false`), the
 *    Enable button is hidden and a short "Notifications not configured
 *    yet" note is shown.
 *  - If the browser does not support Web Push (missing serviceWorker /
 *    PushManager / Notification), the whole card renders a graceful
 *    "This browser does not support push notifications" state.
 *  - Disable = hard delete of THIS device's subscription (both browser
 *    side via `unsubscribe()` and backend via POST /unsubscribe).
 *  - Send test = POST /test, delivers to THIS device only.
 *
 * No cache, no localStorage, no cross-tab state — mount reconciles
 * fresh every time.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";

const SW_URL = "/sw-notifications.js";
const SW_SCOPE = "/care-";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

function browserSupportsPush() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export default function EnablePushNotifications({ roomId }) {
  const supported = useMemo(browserSupportsPush, []);
  const [readiness, setReadiness] = useState(null); // {ready, configured, ...}
  const [permission, setPermission] = useState(() =>
    supported ? Notification.permission : "denied"
  );
  const [subscribedHere, setSubscribedHere] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Reconcile on mount ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported) { setLoading(false); return; }
      try {
        const { data: r } = await api.get("/api/care-rooms/push/readiness");
        if (!cancelled) setReadiness(r);
        if (!r?.ready) { setLoading(false); return; }

        // Register the SW under the /care- scope. Idempotent.
        const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
        // Do NOT prompt for permission — the user must press Enable.
        const browserSub = await reg.pushManager.getSubscription();
        if (cancelled) return;
        setSubscribedHere(!!browserSub);
      } catch (e) {
        if (!cancelled) {
          // Non-fatal — just show the component in "unknown state" and
          // let the user press Enable to retry.
          console.warn("[push] reconcile failed", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      // 1. Permission — ONLY here, only on user gesture.
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications permission was not granted.");
        setBusy(false);
        return;
      }

      // 2. Fetch VAPID public key.
      const { data: vapid } = await api.get("/api/care-rooms/push/vapid-public-key");
      if (vapid?.push_disabled || !vapid?.public_key) {
        toast.error("Notifications aren't configured on the server yet.");
        setBusy(false);
        return;
      }

      // 3. Ensure SW + browser subscription.
      const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
        });
      }

      // 4. Register with backend.
      const raw = sub.toJSON();
      await api.post("/api/care-rooms/push/subscribe", {
        endpoint: raw.endpoint,
        keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
        user_agent: navigator.userAgent || "",
      });

      setSubscribedHere(true);
      toast.success("Notifications enabled on this device.");
    } catch (e) {
      console.error("[push] enable failed", e);
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const disable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const raw = sub.toJSON();
        try {
          await api.post("/api/care-rooms/push/unsubscribe", { endpoint: raw.endpoint });
        } catch (e) { /* backend was ahead — that's fine */ }
        try { await sub.unsubscribe(); } catch (_e) { /* best effort */ }
      }
      setSubscribedHere(false);
      toast.success("Notifications disabled on this device.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not disable notifications.");
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const sendTest = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (!sub) { toast.error("No active subscription on this device."); setBusy(false); return; }
      const raw = sub.toJSON();
      const { data } = await api.post("/api/care-rooms/push/test", { endpoint: raw.endpoint });
      if (data?.ok) toast.success("Test notification sent.");
      else toast.error(`Send failed: ${data?.error || "unknown"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send test.");
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  // ── Render states ────────────────────────────────────────────────
  if (!supported) {
    return (
      <div
        data-testid="push-not-supported"
        className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
      >
        This browser does not support push notifications.
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid="push-loading"
        className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500"
      >
        Checking notification setup…
      </div>
    );
  }

  if (!readiness?.ready) {
    return (
      <div
        data-testid="push-not-configured"
        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
      >
        Notifications aren&apos;t turned on yet for this environment.
      </div>
    );
  }

  return (
    <div
      data-testid="push-controls"
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="mb-2 text-sm font-medium text-slate-800">
        Push notifications on this device
      </div>
      <p className="mb-3 text-xs text-slate-500">
        You&apos;ll get a generic &quot;New message in JCC Care Rooms&quot; alert. No message
        content, sender name, or room details are ever sent to the notification.
      </p>
      <div className="flex flex-wrap gap-2">
        {!subscribedHere && (
          <Button
            data-testid="push-enable-btn"
            size="sm"
            disabled={busy}
            onClick={enable}
          >
            {permission === "denied" ? "Permission blocked — reset in browser" : "Enable notifications"}
          </Button>
        )}
        {subscribedHere && (
          <>
            <Button
              data-testid="push-disable-btn"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={disable}
            >
              Disable notifications
            </Button>
            <Button
              data-testid="push-send-test-btn"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={sendTest}
            >
              Send test notification
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
